/**
 * DailyFocusCard — Spirit-picked single daily focus (gentle, non-overwhelming).
 * Replaces the "wall of 84 missions" with ONE focus per day.
 *
 * Props:
 *   program: "keto_fusion"
 *   phase: current phase object
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function DailyFocusCard({ program = "keto_fusion", phase }) {
  const [today, setToday] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/keto-fusion/today`, { headers: authHeaders() });
      const data = await res.json();
      if (data?.enrolled) setToday(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  if (!today?.mission) return null;
  const m = today.mission;
  const isDone = today.status === "completed" || m?.completed_today;
  const color = phase?.color || "#84cc16";

  const handleComplete = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/keto-fusion/complete-mission`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ week: today.current_week, day: today.current_day }),
      });
      toast.success("Foco concluído ✓");
      load();
    } catch {
      toast.error("Erro ao registar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border relative overflow-hidden mb-6"
      style={{ borderColor: `${color}40`, background: "linear-gradient(135deg, rgba(0,0,0,0.5), rgba(24,24,27,0.9))" }}
      data-testid="daily-focus-card"
    >
      {/* Ambient glow */}
      <motion.div
        className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl pointer-events-none"
        style={{ background: color, opacity: 0.15 }}
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      <div className="relative p-5">
        <div className="flex items-center gap-2 mb-2">
          <motion.div animate={{ rotate: [0, 12, 0] }} transition={{ duration: 4, repeat: Infinity }}>
            <Sun className="w-4 h-4" style={{ color }} />
          </motion.div>
          <p className="text-[10px] uppercase tracking-widest" style={{ color }}>
            Foco do dia · Semana {today.current_week}/12 · Dia {today.current_day}/7
          </p>
        </div>

        <h3 className="text-xl sm:text-2xl font-bold text-zinc-100 mb-2 leading-tight" data-testid="focus-title">
          {m.title}
        </h3>
        {m.focus && (
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">
            {m.focus}
          </p>
        )}
        <p className="text-sm text-zinc-300 leading-relaxed mb-4" data-testid="focus-body">
          {m.body}
        </p>

        {/* Spirit tone bubble */}
        {m.spirit_tone && (
          <div className="inline-flex items-center gap-1.5 mb-4 px-2.5 py-1 rounded-full bg-zinc-900/70 border border-zinc-800">
            <Sparkles className="w-3 h-3 text-emerald-400" />
            <span className="text-[10px] text-zinc-400">Spirit · {m.spirit_tone}</span>
          </div>
        )}

        {/* Hints inline (training + fueling) */}
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {m.training_hint && (
            <div className="p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800">
              <p className="text-[10px] uppercase tracking-widest text-lime-400 mb-0.5">⚡ Treino</p>
              <p className="text-[11px] text-zinc-300 leading-snug">{m.training_hint}</p>
            </div>
          )}
          {m.fueling_hint && (
            <div className="p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800">
              <p className="text-[10px] uppercase tracking-widest text-purple-400 mb-0.5">🍴 Nutrição</p>
              <p className="text-[11px] text-zinc-300 leading-snug">{m.fueling_hint}</p>
            </div>
          )}
        </div>

        {/* CTA */}
        {isDone ? (
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <Check className="w-4 h-4" /> Foco concluído hoje
          </div>
        ) : (
          <Button
            onClick={handleComplete}
            disabled={saving}
            className="w-full font-bold text-black disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${color}, #00ff88)` }}
            data-testid="focus-complete"
          >
            {saving ? "A registar..." : "✓ Concluir foco de hoje"}
          </Button>
        )}
      </div>
    </motion.div>
  );
}
